import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import '../styles/SubscriptionManager.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const d = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const formatAmount = (cents, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
};

const SubscriptionManager = ({ onUpgrade }) => {
  const { token, user } = useAuth();
  const { hasActiveAccess, isTrialActive } = useTrial();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const authToken = token;
      const res = await fetch(`${API}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setSub(data);
    } catch (err) {
      logger.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleCancel = async () => {
    setCanceling(true);
    setMessage(null);
    try {
      const authToken = token;
      const res = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        setShowConfirm(false);
        fetchStatus();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to cancel subscription' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setCanceling(false);
    }
  };

  const handleResubscribe = async () => {
    setResubscribing(true);
    setMessage(null);
    try {
      const authToken = token;
      const res = await fetch(`${API}/subscription/resubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ origin_url: window.location.origin }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        if (data.reactivated) {
          setMessage({ type: 'success', text: data.message });
          fetchStatus();
        } else if (data.url) {
          window.location.href = data.url;
        }
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to re-subscribe' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setResubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="sub-manager" data-testid="subscription-manager">
        <h2><i className="fa-solid fa-crown"></i> Subscription</h2>
        <div className="sub-loading"><div className="sub-spinner"></div></div>
      </div>
    );
  }

  const isAdmin = user?.email === 'chattest@test.com';
  const isSubscribed = sub?.subscribed || sub?.status === 'active';
  const isCanceling = sub?.cancel_at_period_end || sub?.status === 'canceling';

  return (
    <div className="sub-manager" data-testid="subscription-manager">
      <h2><i className="fa-solid fa-crown"></i> Subscription</h2>

      {/* Admin permanent access */}
      {isAdmin && (
        <div className="sub-card sub-card--active" data-testid="sub-admin-badge">
          <div className="sub-card-header">
            <div className="sub-status-badge sub-status--admin">
              <i className="fa-solid fa-shield-halved"></i> Admin Access
            </div>
          </div>
          <p className="sub-description">You have permanent full access to all features.</p>
        </div>
      )}

      {/* Active subscription */}
      {!isAdmin && isSubscribed && (
        <div className={`sub-card ${isCanceling ? 'sub-card--canceling' : 'sub-card--active'}`} data-testid="sub-active-card">
          <div className="sub-card-header">
            <div className={`sub-status-badge ${isCanceling ? 'sub-status--canceling' : 'sub-status--active'}`}>
              <i className={`fa-solid ${isCanceling ? 'fa-clock' : 'fa-circle-check'}`}></i>
              {isCanceling ? ' Canceling' : ' Active'}
            </div>
            <div className="sub-plan-price" data-testid="sub-plan-price">
              {formatAmount(sub.plan_amount || 499, sub.plan_currency || 'usd')}/{sub.plan_interval || 'month'}
            </div>
          </div>
          <div className="sub-plan-name">Full Access Monthly</div>

          <div className="sub-details-grid">
            {sub.current_period_end && (
              <div className="sub-detail-item" data-testid="sub-next-billing">
                <span className="sub-detail-label">
                  {isCanceling ? 'Access until' : 'Next billing date'}
                </span>
                <span className="sub-detail-value">{formatDate(sub.current_period_end)}</span>
              </div>
            )}
            {sub.updated_at && (
              <div className="sub-detail-item">
                <span className="sub-detail-label">Last updated</span>
                <span className="sub-detail-value">{formatDate(sub.updated_at)}</span>
              </div>
            )}
          </div>

          {isCanceling && (
            <div className="sub-canceling-notice" data-testid="sub-canceling-notice">
              <i className="fa-solid fa-info-circle"></i>
              Your subscription will end on {formatDate(sub.current_period_end)}. You'll retain full access until then.
            </div>
          )}

          {isCanceling && (
            <button
              className="sub-resubscribe-btn"
              data-testid="sub-resubscribe-btn"
              onClick={handleResubscribe}
              disabled={resubscribing}
            >
              {resubscribing ? 'Processing...' : 'Re-subscribe'}
            </button>
          )}

          {!isCanceling && (
            <button
              className="sub-cancel-btn"
              data-testid="sub-cancel-btn"
              onClick={() => setShowConfirm(true)}
            >
              Cancel Subscription
            </button>
          )}
        </div>
      )}

      {/* No subscription — show upgrade prompt */}
      {!isAdmin && !isSubscribed && (
        <div className="sub-card sub-card--none" data-testid="sub-no-subscription">
          <div className="sub-card-header">
            <div className="sub-status-badge sub-status--none">
              <i className="fa-solid fa-circle-xmark"></i> No Subscription
            </div>
          </div>
          {isTrialActive ? (
            <p className="sub-description">You're on a free trial. Subscribe to keep full access after it ends.</p>
          ) : (
            <p className="sub-description">Unlock all game modes, skins, and premium features.</p>
          )}
          <div className="sub-upgrade-row">
            <span className="sub-price-highlight">$4.99/month</span>
            <button className="sub-upgrade-btn" data-testid="sub-upgrade-btn" onClick={() => handleResubscribe()}>
              <i className="fa-solid fa-bolt"></i> Subscribe Now
            </button>
          </div>
        </div>
      )}

      {/* Message toast */}
      {message && (
        <div className={`sub-message sub-message--${message.type}`} data-testid="sub-message">
          {message.text}
        </div>
      )}

      {/* Cancel confirmation dialog */}
      {showConfirm && (
        <div className="sub-confirm-overlay" data-testid="sub-cancel-confirm" onClick={() => setShowConfirm(false)}>
          <div className="sub-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription?</h3>
            <p>You'll keep full access until the end of your current billing period ({formatDate(sub?.current_period_end)}). After that, you'll revert to the Basic plan.</p>
            <div className="sub-confirm-actions">
              <button className="sub-confirm-keep" data-testid="sub-cancel-keep" onClick={() => setShowConfirm(false)}>
                Keep Subscription
              </button>
              <button
                className="sub-confirm-cancel"
                data-testid="sub-cancel-confirm-btn"
                onClick={handleCancel}
                disabled={canceling}
              >
                {canceling ? 'Canceling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
